/**
 * Port von `IwmDropdownContainer.razor` samt `ContainerAdaptor`: Die Liste wird
 * seitenweise über die View-API nachgeladen (`Query().Take(10).RequiresCount()`), der
 * Suchtext filtert serverseitig über `ContainerNo`.
 */

import { useCallback, useRef } from 'react';

import { containerApi } from '@/api/services/containerApi';
import { useAuth } from '@/auth/useAuth';
import { DropDownList, type LoadItemsRequest, type LoadItemsResult } from '@/components/ui';
import type { Container } from '@/types/entities';
import { EntityTypes } from '@/types/enums';
import { formatDate } from '@/utils/date';

export interface ContainerDropdownProps {
  value: number | null;
  onChange(value: number | null): void;
  readOnly?: boolean;
  /** Feste Beschriftung über dem Feld (REQ-24 §2). */
  label?: string;
  /** Standard: `auth.getViewIdByEntity(EntityTypes.Container)` */
  viewId?: number | null;
}

/** `Query().Take(10)` des Altclients. */
const PAGE_SIZE = 10;
const FIELDS = { value: 'Id', text: 'ContainerNo' } as const;
const EMPTY_RESULT: LoadItemsResult<Container> = { items: [], count: 0 };

/**
 * Eintragsvorlage 1:1 aus dem Altclient. Die Klassen sind dort unstyled, gehören aber zum
 * DOM und bleiben deshalb global (kein CSS-Modul).
 */
function containerItem(container: Container) {
  return (
    <span>
      <span className="name">{container.ContainerNo}</span>
      <span className="typ">{container.ContTyp}</span>
      <span className="empf">{container.Empfaenger}</span>
      <span className="liefertermin">{formatDate(container.LieferterminContainer)}</span>
      <span className="id">| {container.Id}</span>
    </span>
  );
}

export function ContainerDropdown(props: ContainerDropdownProps) {
  const auth = useAuth();
  const defaultViewId = auth.getViewIdByEntity(EntityTypes.Container);
  const viewId = props.viewId === undefined ? defaultViewId : props.viewId;

  // Wie `ChinaClientDataAdaptorBase`: `maxcount` wird nur neu geholt, wenn sich der
  // Where-Ausdruck geändert hat.
  const previousCount = useRef<{ whereQuery: string; count: number } | null>(null);

  const loadItems = useCallback(
    async (request: LoadItemsRequest): Promise<LoadItemsResult<Container>> => {
      if (viewId === null) return EMPTY_RESULT;

      const search = request.search.trim();
      const whereQuery = search === '' ? '' : `ContainerNo contains "${search}"`;
      const page = Math.floor(request.skip / request.take) + 1;

      try {
        const cached = previousCount.current;
        const count =
          cached !== null && cached.whereQuery === whereQuery
            ? cached.count
            : await containerApi.getMaxCount(viewId, whereQuery);
        previousCount.current = { whereQuery, count };

        const items = await containerApi.get(viewId, { pageSize: request.take, page, whereQuery });
        return { items, count };
      } catch {
        return EMPTY_RESULT;
      }
    },
    [viewId],
  );

  // Der gewählte Container steht nach dem Neuladen selten auf der ersten Seite; die
  // Anzeige holt ihn dann einzeln nach (Ersatz für `EnableVirtualization`).
  const resolveValue = useCallback(
    async (id: number): Promise<Container | null> => {
      if (viewId === null) return null;
      try {
        return await containerApi.getEntry(viewId, id);
      } catch {
        return null;
      }
    },
    [viewId],
  );

  return (
    <DropDownList<number, Container>
      value={props.value}
      onChange={(value) => {
        props.onChange(value);
      }}
      loadItems={loadItems}
      resolveValue={resolveValue}
      fields={FIELDS}
      itemTemplate={containerItem}
      pageSize={PAGE_SIZE}
      label={props.label}
      readOnly={props.readOnly}
      allowFiltering
      showClearButton
    />
  );
}

export default ContainerDropdown;
